import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import type { Bond } from '../api';
import { usePendencias } from '../hooks';
import { CONTRACT_KIND_LABELS, ORIGIN_LABELS, formatDate, manualStatusTargets } from './format';
import { BondStatusBadge } from './StatusBadge';

const PRE_ACTIVE = new Set([
  'admissao_em_preparacao', 'aguardando_documentos', 'aguardando_exame_admissional',
  'aguardando_contabilidade', 'aguardando_assinatura', 'admissao_confirmada',
]);

export type BondAction =
  | 'editar' | 'cancelar' | 'exportar' | 'registrar-folha' | 'conciliar' | 'confirmar' | 'ativar' | 'situacao';

interface Props {
  bond: Bond;
  busy?: boolean;
  onAction: (action: BondAction, bond: Bond) => void;
}

function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

/**
 * Um vínculo com as ações permitidas na situação atual. As ações espelham
 * `bond-status-machine.ts`; o backend recusa o que não couber.
 */
export function BondCard({ bond, busy, onAction }: Props) {
  const { can } = usePermissions();
  const podeEditar = can(FEATURES.pessoasColaboradores, 'edit');
  const podeExportar = podeEditar && can(FEATURES.pessoasDadosSensiveis, 'view');
  const preAtivo = PRE_ACTIVE.has(bond.status);
  const pendencias = usePendencias(bond.id, !bond.closed);
  const clt = bond.employmentType === 'clt';

  const acoes: Array<{ key: BondAction; label: string; variant?: 'primary' | 'secondary' | 'danger'; show: boolean }> = [
    { key: 'exportar', label: bond.status === 'aguardando_contabilidade' ? 'Exportar novamente' : 'Exportar admissão',
      show: podeExportar && clt && (bond.status === 'admissao_em_preparacao' || bond.status === 'aguardando_contabilidade') },
    { key: 'registrar-folha', label: 'Registrar matrícula da folha', variant: 'primary',
      show: podeEditar && clt && !bond.closed && bond.status !== 'admissao_em_preparacao' && !bond.reconciliationPending },
    { key: 'conciliar', label: 'Conciliar divergência', variant: 'primary', show: podeEditar && bond.reconciliationPending },
    { key: 'confirmar', label: 'Confirmar admissão', variant: 'primary',
      show: podeEditar && !clt && bond.status === 'admissao_em_preparacao' },
    { key: 'ativar', label: 'Ativar vínculo', variant: 'primary', show: podeEditar && bond.status === 'admissao_confirmada' },
    { key: 'situacao', label: 'Alterar situação',
      show: can(FEATURES.pessoasSituacaoFuncional, 'edit') && manualStatusTargets(bond.status).length > 0 },
    { key: 'editar', label: 'Editar contrato', show: podeEditar && preAtivo },
    { key: 'cancelar', label: 'Cancelar contratação', variant: 'danger', show: podeEditar && preAtivo },
  ];

  const lista = pendencias.data ?? [];

  return (
    <article className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Matrícula {bond.internalRegistration}
            <span className="ml-2 text-sm font-normal text-gray-500">
              {bond.company?.tradeName ?? '—'} · {bond.employmentType.toUpperCase()}
            </span>
          </h3>
          <p className="text-xs text-gray-500">Origem: {ORIGIN_LABELS[bond.origin] ?? bond.origin}</p>
        </div>
        <BondStatusBadge status={bond.status} />
      </header>

      <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Campo label="Admissão" value={formatDate(bond.admissionDate)} />
        <Campo label="Contrato" value={CONTRACT_KIND_LABELS[bond.contractKind]} />
        {bond.contractKind === 'determinado' && <Campo label="Fim previsto" value={formatDate(bond.contractEndDate)} />}
        {bond.contractKind === 'experiencia' && (
          <>
            <Campo label="Fim da experiência" value={formatDate(bond.probationEndDate)} />
            <Campo label="Fim da prorrogação" value={formatDate(bond.probationExtensionEndDate)} />
          </>
        )}
        {clt && <Campo label="Matrícula da folha" value={bond.payrollRegistration ?? '—'} />}
        {bond.endDate && <Campo label="Encerramento" value={formatDate(bond.endDate)} />}
      </dl>

      {bond.reconciliationPending && (
        <p className="flex items-center gap-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          O retorno da contabilidade divergiu do valor registrado. Veja o histórico e concilie.
        </p>
      )}

      {!bond.closed && (
        <div>
          <h4 className="mb-1 text-sm font-medium text-gray-700">Pendências</h4>
          {pendencias.isLoading ? (
            <p className="text-sm text-gray-500">Verificando…</p>
          ) : lista.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" /> Nenhuma pendência impeditiva.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {lista.map((p) => (
                <li key={p.code} className="flex items-start gap-2 text-gray-700">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <span>
                    {p.message}
                    {p.stage === 'ativacao' && <span className="ml-1 text-xs text-gray-500">(bloqueia só a ativação)</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {acoes.some((a) => a.show) && (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-3">
          {acoes.filter((a) => a.show).map((a) => (
            <Button key={a.key} size="sm" variant={a.variant ?? 'secondary'} disabled={busy} onClick={() => onAction(a.key, bond)}>
              {a.label}
            </Button>
          ))}
        </footer>
      )}
    </article>
  );
}
